import React, { useEffect, useMemo, useState } from 'react';
/**
 * MultiplicationGridRenderer — Fill hidden cells in a multiplication table.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { SparklesIcon } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
interface EditableCell {
  key: string;
  row: number;
  col: number;
  rowValue: number;
  colValue: number;
}
export function MultiplicationGridRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const mode = config.mode || 'single_table';
  const [filledCells, setFilledCells] = useState<Record<string, string>>({});
  const editableCells = useMemo<EditableCell[]>(() => {
    if (mode === 'single_table') {
      const baseNumber = config.baseNumber || 1;
      const range: number[] = config.range || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      const hiddenCells: number[] = config.hiddenCells || [];
      return hiddenCells.filter(index => range[index] !== undefined).map(index => ({
        key: String(index),
        row: 0,
        col: index,
        rowValue: baseNumber,
        colValue: range[index]
      }));
    }
    const rows: number[] = config.rows || [];
    const cols: number[] = config.cols || [];
    const hiddenCells: Array<{
      row: number;
      col: number;
    }> = config.hiddenCells || [];
    return hiddenCells.filter(cell => rows[cell.row] !== undefined && cols[cell.col] !== undefined).map(cell => ({
      key: `${cell.row}-${cell.col}`,
      row: cell.row,
      col: cell.col,
      rowValue: rows[cell.row],
      colValue: cols[cell.col]
    }));
  }, [config, mode]);
  useEffect(() => {
    setFilledCells({});
  }, [question.id]);
  const totalHiddenCells = editableCells.length;
  useEffect(() => {
    const filledCount = Object.values(filledCells).filter(value => value.trim() !== '').length;
    onReadyChange?.(filledCount === totalHiddenCells && totalHiddenCells > 0);
  }, [filledCells, totalHiddenCells, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(filledCells), [filledCells]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleInputChange = (key: string, value: string) => {
    setFilledCells(previous => ({
      ...previous,
      [key]: value
    }));
  };
  const focusCell = (key: string) => {
    document.getElementById(`multiplication-grid-${question.id}-${key}`)?.focus();
  };
  const findDirectionalCell = (current: EditableCell, direction: 'left' | 'right' | 'up' | 'down') => {
    const candidates = editableCells.filter(cell => {
      if (direction === 'left') {
        return cell.row === current.row && cell.col < current.col;
      }
      if (direction === 'right') {
        return cell.row === current.row && cell.col > current.col;
      }
      if (direction === 'up') {
        return cell.col === current.col && cell.row < current.row;
      }
      return cell.col === current.col && cell.row > current.row;
    });
    return candidates.sort((a, b) => {
      const aDistance = direction === 'left' || direction === 'right' ? Math.abs(a.col - current.col) : Math.abs(a.row - current.row);
      const bDistance = direction === 'left' || direction === 'right' ? Math.abs(b.col - current.col) : Math.abs(b.row - current.row);
      return aDistance - bDistance;
    })[0];
  };
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, cell: EditableCell) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const index = editableCells.findIndex(candidate => candidate.key === cell.key);
      const nextCell = editableCells[index + 1];
      if (nextCell) {
        focusCell(nextCell.key);
      } else {
        event.currentTarget.blur();
      }
      return;
    }
    if (!event.altKey) return;
    const directionByKey: Record<string, 'left' | 'right' | 'up' | 'down'> = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowUp: 'up',
      ArrowDown: 'down'
    };
    const direction = directionByKey[event.key];
    if (!direction) return;
    const nextCell = findDirectionalCell(cell, direction);
    if (nextCell) {
      event.preventDefault();
      focusCell(nextCell.key);
    }
  };
  const filledCount = Object.values(filledCells).filter(value => value.trim() !== '').length;
  const cellInputClass = (value: string) => `w-full h-full text-center font-bold rounded-md outline-none transition-all duration-200 ${value ? 'bg-indigo-50 border-2 border-indigo-300 text-indigo-800' : 'bg-amber-50 border-2 border-amber-300 text-gray-800'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white shadow-inner`;
  const headerCellClass = 'w-11 h-11 sm:w-12 sm:h-12 bg-indigo-600 text-white font-bold rounded-md text-center align-middle shadow-sm';
  const dataCellClass = 'w-11 h-11 sm:w-12 sm:h-12 bg-white border border-gray-200 text-gray-800 font-medium rounded-md text-center align-middle shadow-sm';
  const renderInput = (cell: EditableCell, headerIds: string[]) => <input id={`multiplication-grid-${question.id}-${cell.key}`} type="text" inputMode="numeric" pattern="[0-9]*" value={filledCells[cell.key] || ''} onChange={event => handleInputChange(cell.key, event.target.value)} onKeyDown={event => handleInputKeyDown(event, cell)} disabled={shouldValidate} aria-label={`Antwoord voor ${cell.rowValue} keer ${cell.colValue}`} aria-describedby={headerIds.join(' ')} className={cellInputClass(filledCells[cell.key] || '')} data-id="element-12325" data-mp="2l72orqqtx2kh" />;
  const renderSingleTable = () => {
    const baseNumber = config.baseNumber || 1;
    const range: number[] = config.range || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const hiddenCells: number[] = config.hiddenCells || [];
    const tableId = `multiplication-grid-${question.id}`;
    return <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-12326" data-mp="1jqv55a5cu5ch">
        <table className="border-separate border-spacing-1 w-max mx-auto" data-id="element-12327" data-mp="1joinky3idwzy">
          <caption className="sr-only" data-id="element-12328" data-mp="3iutjq7p3g8k9">
            Tafel van {baseNumber}. Vul de lege uitkomsten in.
          </caption>
          <thead data-id="element-12329" data-mp="3nrtq6jpuvdbw">
            <tr data-id="element-12330" data-mp="3sl6fkw2czo5p">
              <th id={`${tableId}-corner`} scope="col" className="w-11 h-11 sm:w-12 sm:h-12 bg-indigo-800 text-white font-bold rounded-md text-center align-middle shadow-sm" data-id="element-12331" data-mp="3skmvb8p8knoi">
                
                ×
              </th>
              {range.map((multiplier, index) => <th key={`header-${index}`} id={`${tableId}-column-${index}`} scope="col" className={headerCellClass} data-id="element-12332" data-mp="2zylj5h710lva" data-mpi={index}>
                
                  {multiplier}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-12333" data-mp="3eu18j8wnvtlc">
            <tr data-id="element-12334" data-mp="3eujdqv1axi5e">
              <th id={`${tableId}-row-0`} scope="row" className={headerCellClass} data-id="element-12335" data-mp="3ev2x8g6loelj">
                
                {baseNumber}
              </th>
              {range.map((multiplier, index) => {
              if (hiddenCells.includes(index)) {
                const cell = editableCells.find(candidate => candidate.key === String(index));
                return <td key={`cell-${index}`} className="w-11 h-11 sm:w-12 sm:h-12" data-id="element-12336" data-mp="29mgujyx2pnox">
                      
                      {cell && renderInput(cell, [`${tableId}-row-0`, `${tableId}-column-${index}`])}
                    </td>;
              }
              return <td key={`cell-${index}`} className={dataCellClass} data-id="element-12337" data-mp="1zvvvybhegj60" data-mpi={index}>
                    {baseNumber * multiplier}
                  </td>;
            })}
            </tr>
          </tbody>
        </table>
      </div>;
  };
  const renderCrossTable = () => {
    const rows: number[] = config.rows || [];
    const cols: number[] = config.cols || [];
    const hiddenCells: Array<{
      row: number;
      col: number;
    }> = config.hiddenCells || [];
    const tableId = `multiplication-grid-${question.id}`;
    const isHidden = (row: number, col: number) => hiddenCells.some(cell => cell.row === row && cell.col === col);
    return <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-12338" data-mp="3b9relu7ija1z">
        <table className="border-separate border-spacing-1 w-max mx-auto" data-id="element-12339" data-mp="2k6q99vs6ctho">
          <caption className="sr-only" data-id="element-12340" data-mp="2p49qn4k65mdu">
            Kruistabel. Vul de lege uitkomsten van de vermenigvuldigingen in.
          </caption>
          <thead data-id="element-12341" data-mp="2aec6r0t9viug">
            <tr data-id="element-12342" data-mp="2ffi1132kze63">
              <th id={`${tableId}-corner`} scope="col" className="w-11 h-11 sm:w-12 sm:h-12 bg-indigo-800 text-white font-bold rounded-md text-center align-middle shadow-sm" data-id="element-12343" data-mp="2fezw7i1urrmk">
                
                ×
              </th>
              {cols.map((columnValue, index) => <th key={`column-${index}`} id={`${tableId}-column-${index}`} scope="col" className={headerCellClass} data-id="element-12344" data-mp="26cqo0yu24acc" data-mpi={index}>
                
                  {columnValue}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-12345" data-mp="3lvtrsj32ptrn">
            {rows.map((rowValue, rowIndex) => <tr key={`row-${rowIndex}`} data-id="element-12346" data-mp="3lw3hs6o4c0xc" data-mpi={rowIndex}>
                <th id={`${tableId}-row-${rowIndex}`} scope="row" className={headerCellClass} data-id="element-12347" data-mp="3quonglm53ct6">
                
                  {rowValue}
                </th>
                {cols.map((columnValue, colIndex) => {
              if (isHidden(rowIndex, colIndex)) {
                const cell = editableCells.find(candidate => candidate.key === `${rowIndex}-${colIndex}`);
                return <td key={`cell-${rowIndex}-${colIndex}`} className="w-11 h-11 sm:w-12 sm:h-12" data-id="element-12348" data-mp="1oyxupvvx0y4r">
                      
                        {cell && renderInput(cell, [`${tableId}-row-${rowIndex}`, `${tableId}-column-${colIndex}`])}
                      </td>;
              }
              return <td key={`cell-${rowIndex}-${colIndex}`} className={dataCellClass} data-id="element-12349" data-mp="1tvy2qccbsayi" data-mpi={colIndex}>
                    
                      {rowValue * columnValue}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>;
  };
  const progressPct = totalHiddenCells > 0 ? filledCount / totalHiddenCells * 100 : 0;
  return <div className="w-full flex flex-col space-y-4" data-id="element-12350" data-mp="8jejl8q9tnml">
      <p className="text-center text-sm text-slate-500" data-id="element-12351" data-mp="8kzcktwxsyic">
        Vul de lege vakjes in. Gebruik Enter voor het volgende vakje of Alt met
        de pijltjestoetsen om door een rij of kolom te bewegen.
      </p>
      <div className="w-full bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-100 shadow-inner" data-id="element-12352" data-mp="u5z77mdw86zl">
        {mode === 'single_table' ? renderSingleTable() : renderCrossTable()}
      </div>
      <div className="flex flex-col items-center justify-center space-y-2" data-id="element-12353" data-mp="l513z6ba43i2">
        <div className="flex items-center space-x-2 text-sm font-medium text-gray-600" data-id="element-12354" data-mp="1hsp98nq8s1k7">
          <SparklesIcon className={`w-4 h-4 ${filledCount === totalHiddenCells ? 'text-amber-500' : 'text-gray-400'}`} aria-hidden="true" data-id="element-12355" data-mp="1cvf84athq1gj" />
          
          <span aria-live="polite" data-id="element-12356" data-mp="141sg8vselicm">
            {filledCount} van {totalHiddenCells} ingevuld
          </span>
        </div>
        <div className="w-48 h-2 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-label="Voortgang ingevulde vakjes" aria-valuemin={0} aria-valuemax={totalHiddenCells} aria-valuenow={filledCount} data-id="element-12357" data-mp="20jyicvw3oob9">
          
          <div className="h-full bg-indigo-500 transition-all duration-300 ease-out" style={{
          width: `${progressPct}%`
        }} data-id="element-12358" data-mp="3eh1f1z9vkcp1" />
          
        </div>
      </div>
    </div>;
}